// src/pages/agents/AgentOverviewTab.js
import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Paper, Typography, Button, CircularProgress, Alert, Stack, Divider, Tooltip, useTheme // Added useTheme
} from '@mui/material';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { agentService } from '../../api/agentService';
import { promptService } from '../../api/promptService';
// import { widgetService } from '../../api/widgetService'; // Not directly used for counts in this update
import { conversationService } from '../../api/conversationService'; // For agent-specific convos
import {
    PlayArrow as PlayArrowIcon,
    Add as AddIcon,
    Visibility as VisibilityIcon,
    Code as CodeIcon,
    Forum as ForumIcon, // For Conversations
    Widgets as WidgetsIcon, // For Widgets
    Build as ToolsIcon, // For Tools
    SpeakerNotes as PromptIcon, // For Prompt
    Memory as LLMIcon // For LLM Model
} from '@mui/icons-material';
// apiClient not directly used here, services handle it

// Helper component for summary cards
const SummaryCard = ({ title, value, icon, loading = false, onClick, ctaLabel }) => {
  const theme = useTheme();
  return (
    <Paper 
      sx={{ 
        p: 2, 
        display: 'flex', 
        flexDirection: 'column', // Stack icon+title and value+cta
        justifyContent: 'space-between',
        height: '100%',
        '&:hover': onClick ? { boxShadow: theme.shadows[2], cursor:'pointer' } : {},
        transition: 'box-shadow 0.2s ease-in-out'
      }}
      onClick={onClick}
      elevation={1}
    >
      <Box display="flex" alignItems="center" mb={onClick ? 0.5 : 1.5}> {/* Less mb if clickable */}
        {icon && <Box sx={{ mr: 1.5, color: 'text.secondary', display:'flex', alignItems:'center' }}>{React.cloneElement(icon, {fontSize: 'medium'})}</Box>}
        <Typography variant="body2" color="text.secondary" display="block">
          {title}
        </Typography>
      </Box>
      <Typography variant="h6" component="div" noWrap sx={{ mb: onClick ? 0.5 : 0 }}>
        {loading ? <CircularProgress size={20} /> : (value || 'N/A')}
      </Typography>
      {onClick && ctaLabel && (
        <Typography variant="caption" color="primary" sx={{mt:'auto', alignSelf:'flex-end', fontWeight: 500}}>
          {ctaLabel}
        </Typography>
      )}
    </Paper>
  );
};


const AgentOverviewTab = () => {
  const { agent } = useOutletContext();
  const navigate = useNavigate();

  const [summary, setSummary] = useState({
    promptName: null,
    toolsCount: 0,
    widgetsCount: 0, // Still a placeholder, but UI can be prepared
    convosCount: 0,
  });
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSummaryData = async () => {
      if (!agent || !agent.id) {
        // setError("Agent data not available yet.");
        setLoadingSummary(false); // Stop loading if no agent id
        return;
      }

      setLoadingSummary(true);
      setError(null);
      let promptNameData = 'None';
      let toolsCountData = 0;
      let convosCountData = 0;
      // let widgetsCountData = 0; // If fetching widgets

      try {
        // Fetch Prompt Name
        if (agent.system_prompt_id) {
          const promptRes = await promptService.getPromptById(agent.system_prompt_id);
          if (!promptRes.isError && promptRes.name) {
            promptNameData = promptRes.name;
          } else {
            console.warn(`Could not fetch prompt name for ID: ${agent.system_prompt_id}`);
            promptNameData = `ID: ${agent.system_prompt_id.substring(0, 8)}...`;
          }
        }

        // Fetch Tools Count
        const toolsRes = await agentService.getAgentTools(agent.id);
        toolsCountData = toolsRes.isError ? 'Error' : (Array.isArray(toolsRes) ? toolsRes.length : 0);

        // Fetch Agent-Specific Conversations Count
        // Assuming listConversations takes (agentId, limit, offset) and returns { items: [], total: number }
        const convosRes = await conversationService.listConversations(agent.id, 1, 0); // limit 1, offset 0 to get total
        if (convosRes.isError) {
          console.error("Error fetching agent conversations count:", convosRes.message);
          convosCountData = 'Error';
        } else {
          convosCountData = convosRes.total !== undefined ? convosRes.total : 0;
        }
        
        // TODO: Fetch Widgets Count specific to this agent (if API supports it)
        // For now, it's a placeholder
        // const widgetsRes = await widgetService.getWidgetsForAgent(agent.id); // Hypothetical
        // widgetsCountData = widgetsRes.isError ? 'Error' : (widgetsRes.length || 0);


        setSummary({
          promptName: promptNameData,
          toolsCount: toolsCountData,
          widgetsCount: summary.widgetsCount, // Keep placeholder or update if fetched
          convosCount: convosCountData,
        });

      } catch (err) {
        console.error("Error loading agent summary:", err);
        setError('Error loading some summary data. Details may be incomplete.');
        // Set individual error states or keep previously fetched data
        setSummary(prev => ({
          ...prev, // Keep any successfully fetched data
          promptName: prev.promptName || 'Error',
          toolsCount: prev.toolsCount || 'Error',
          convosCount: prev.convosCount || 'Error',
        }));
      } finally {
        setLoadingSummary(false);
      }
    };

    fetchSummaryData();
  }, [agent]); // Depend on agent object (specifically agent.id)

  const handleNavigateToTab = (tabPath) => {
    navigate(`/agents/${agent.id}/${tabPath}`);
  };


  if (!agent) {
     return <Box sx={{display:'flex', justifyContent:'center', alignItems:'center', p:3}}><CircularProgress /><Typography sx={{ml:1}}>Loading agent details...</Typography></Box>;
  }
   if (loadingSummary && !summary.promptName) { // Initial full load state
    return <Box sx={{display:'flex', justifyContent:'center', alignItems:'center', p:3}}><CircularProgress /><Typography sx={{ml:1}}>Loading agent overview...</Typography></Box>;
  }

  return (
    <Box>
      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard title="LLM Model" value={agent.llm_model_name || agent.llm_model || 'Not Set'} icon={<LLMIcon />} loading={loadingSummary && !agent.llm_model_name}/>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard title="Active Prompt" value={summary.promptName} icon={<PromptIcon />} loading={loadingSummary && summary.promptName === null} onClick={() => handleNavigateToTab('prompt')} ctaLabel="Manage"/>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard title="Tools Assigned" value={summary.toolsCount} icon={<ToolsIcon/>} loading={loadingSummary && summary.toolsCount === 0 && typeof summary.toolsCount !== 'string'} onClick={() => handleNavigateToTab('tools')} ctaLabel="Manage"/>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard title="Conversations" value={summary.convosCount} icon={<ForumIcon/>} loading={loadingSummary && summary.convosCount === 0 && typeof summary.convosCount !== 'string'} onClick={() => handleNavigateToTab('conversations')} ctaLabel="View"/>
        </Grid>
         <Grid item xs={12} sm={6} md={3}> 
          {/* Widget count is still a placeholder value in summary state */}
          <SummaryCard title="Widget Versions" value={summary.widgetsCount} icon={<WidgetsIcon />} loading={loadingSummary && summary.widgetsCount === 0} onClick={() => handleNavigateToTab('widgets')} ctaLabel="Manage"/>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

       {/* Quick Actions */}
      <Box mb={3}>
        <Typography variant="h6" mb={2}>Common Actions</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
           <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            // onClick={handleStartTestChat} // Needs implementation
            disabled={true} // Keep disabled until implemented
            sx={{minWidth: 150}}
          >
            Test Chat
          </Button>
          <Button
            variant="outlined"
            startIcon={<CodeIcon />}
            // onClick={handleIssueWAT} // Needs implementation
            disabled={true} // Keep disabled until implemented
            sx={{minWidth: 150}}
          >
            Issue Test Token
          </Button>
           <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => handleNavigateToTab('conversations')}
            disabled={loadingSummary || summary.convosCount === 0 || summary.convosCount === 'Error'}
            sx={{minWidth: 150}}
          >
            View Conversations
          </Button>
           <Button
            variant="outlined"
            startIcon={<AddIcon />} // Could be WidgetsIcon
            onClick={() => handleNavigateToTab('widgets')}
            sx={{minWidth: 150}}
          >
            Manage Widgets
          </Button>
           <Button
            variant="outlined"
            startIcon={<AddIcon />} // Could be ToolsIcon
            onClick={() => handleNavigateToTab('tools')}
            sx={{minWidth: 150}}
          >
            Manage Tools
          </Button>
        </Stack>
      </Box>

      {/* More sections can be added here, e.g., recent activity, performance metrics */}

    </Box>
  );
};

export default AgentOverviewTab;